import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy, Check, ShieldAlert } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { SettingsBreadcrumb } from '@/components/SettingsBreadcrumb';
import { ROUTE } from '@/config';
import { useCreateApiToken } from '@/hooksApi';

interface CreatedToken {
  api_key: string;
  api_secret: string;
}

const CopyableField = ({
  label,
  value,
  mono = true,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className='space-y-1.5'>
      <p className='text-[11px] font-medium text-sd-muted-foreground uppercase tracking-wider'>
        {label}
      </p>
      <div className='flex items-center gap-2 rounded-lg bg-sd-muted px-3 py-2.5'>
        <code
          className={`flex-1 text-xs ${mono ? 'font-mono' : ''} text-sd-foreground select-all break-all`}
        >
          {value}
        </code>
        <button
          onClick={handleCopy}
          className='shrink-0 p-1.5 rounded-md text-sd-muted-foreground hover:text-sd-foreground hover:bg-sd-background transition-colors'
          title='Copy'
        >
          {copied ? <Check className='h-4 w-4 text-green-600' /> : <Copy className='h-4 w-4' />}
        </button>
      </div>
    </div>
  );
};

const SettingsAccessTokensCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutate: createApiToken, isLoading: isCreating } = useCreateApiToken();

  const [tokenName, setTokenName] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
  const [createdToken, setCreatedToken] = useState<CreatedToken | null>(null);

  const handleCancel = () => {
    navigate(ROUTE.SETTINGS_ACCESS_TOKENS);
  };

  const handleCreate = () => {
    const errors: Record<string, boolean> = {};
    if (!tokenName.trim()) errors.token_name = true;

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast({
        title: t('required_fields_missing'),
        description: t('please_enter_token_name'),
        variant: 'destructive',
      });
      return;
    }

    createApiToken(tokenName.trim(), {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['api-token']);
        const data = response?.object;
        if (data?.api_key && data?.api_secret) {
          setCreatedToken({ api_key: data.api_key, api_secret: data.api_secret });
        } else {
          toast({ title: t('token_created') });
          navigate(ROUTE.SETTINGS_ACCESS_TOKENS);
        }
      },
      onError: (error: any) => {
        const msg = error?.response?.data?.message || error?.message || t('operation_failed');
        toast({ title: t('error_title'), description: msg, variant: 'destructive' });
      },
    });
  };

  const inputClass = (field?: string) =>
    `w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-sd-ring focus:border-transparent placeholder:text-sd-muted-foreground ${
      field && fieldErrors[field] ? 'border-red-500 bg-red-50' : 'border-sd-input bg-sd-input-bg'
    }`;

  // ─── One-time credentials view ───
  if (createdToken) {
    return (
      <div className='sd-scope sd-font flex flex-col'>
        <div className='p-6 space-y-6 max-w-3xl w-full mx-auto'>
          <SettingsBreadcrumb
            moduleName='access_tokens'
            moduleRoute={ROUTE.SETTINGS_ACCESS_TOKENS}
            action='create_token'
          />

          <h1 className='text-xl font-semibold text-sd-foreground'>
            {t('your_new_personal_access_token')}
          </h1>

          {/* Warning Banner */}
          <div className='flex items-start gap-3 rounded-2xl border border-solid px-4 py-3' style={{ borderColor: 'rgba(245,158,11,0.3)', backgroundColor: 'rgba(245,158,11,0.05)' }}>
            <ShieldAlert className='h-5 w-5 shrink-0 mt-0.5' style={{ color: 'rgb(217,119,6)' }} />
            <div className='space-y-0.5'>
              <p className='text-sm font-semibold' style={{ color: 'rgb(146,64,14)' }}>
                {String(t('save_your_credentials', 'Save your credentials now'))}
              </p>
              <p className='text-xs' style={{ color: 'rgb(180,83,9)' }}>
                {String(
                  t(
                    'token_secret_warning',
                    'Make sure to copy your API secret now. For security reasons, it won\u2019t be shown again.',
                  ),
                )}
              </p>
            </div>
          </div>

          <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm'>
            <div className='px-6 py-4 border-b border-sd-border'>
              <h2 className='text-sm font-semibold text-sd-foreground'>{tokenName}</h2>
            </div>
            <div className='p-6 space-y-5'>
              <CopyableField label={String(t('api_key'))} value={createdToken.api_key} />
              <CopyableField label={String(t('api_secret'))} value={createdToken.api_secret} />
            </div>
            <div className='px-6 py-4 border-t border-sd-border flex justify-end'>
              <button
                onClick={handleCancel}
                className='px-6 py-2.5 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity'
              >
                {t('done', 'Done')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Creation form ───
  return (
    <div className='sd-scope sd-font flex flex-col'>
      <div className='p-6 space-y-6 max-w-3xl w-full mx-auto'>
        <SettingsBreadcrumb
          moduleName='access_tokens'
          moduleRoute={ROUTE.SETTINGS_ACCESS_TOKENS}
          action='create_token'
        />

        <h1 className='text-xl font-semibold text-sd-foreground'>{t('create_token')}</h1>

        <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm'>
          <div className='px-6 py-4 border-b border-sd-border flex items-center gap-3'>
            <button
              onClick={handleCancel}
              className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
            >
              <ArrowLeft className='h-4 w-4' />
            </button>
            <h2 className='text-sm font-semibold text-sd-foreground'>{t('token_details')}</h2>
          </div>
          <div className='p-6 space-y-5'>
            <div className='space-y-1.5'>
              <label className='text-xs font-medium text-sd-muted-foreground'>
                {t('token_name')} *
              </label>
              <input
                type='text'
                value={tokenName}
                onChange={(e) => {
                  setTokenName(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, token_name: false }));
                }}
                placeholder={t('enter_token_name')}
                className={inputClass('token_name')}
              />
              {fieldErrors.token_name && (
                <p className='text-xs text-red-500 mt-1'>{t('this_field_is_required')}</p>
              )}
            </div>
          </div>
          <div className='px-6 py-4 border-t border-sd-border flex justify-end gap-3'>
            <button
              onClick={handleCancel}
              className='px-4 py-2.5 rounded-lg border border-sd-input bg-sd-background text-sm font-medium hover:bg-sd-muted transition-colors'
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleCreate}
              disabled={isCreating}
              className='px-6 py-2.5 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed'
            >
              {isCreating ? t('loading') : t('generate_token')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsAccessTokensCreate;
